// src/hooks/integrations/synapse/useSynapseWorkflows.ts
import { useCallback, useState } from 'react';

import useSynapseImport from './useSynapseImport';

interface FileStatus {
    fileName: string;
    status: string;
}

export interface WorkflowStatus {
    sessionId: string;
    createdAt: string;
    files: FileStatus[];
    overallStatus: string;
}

interface UseSynapseWorkflowsProps {
    experimentId?: string;
}

/**
 * Hook for managing Synapse import workflows
 */
const useSynapseWorkflows = ({ experimentId }: UseSynapseWorkflowsProps) => {
    const [existingWorkflows, setExistingWorkflows] = useState<WorkflowStatus[]>([]);
    const [isLoadingWorkflows, setIsLoadingWorkflows] = useState<boolean>(false);

    const { getWorkflows, getAllWorkflowFiles, getWorkflowStatus } = useSynapseImport();

    // Function to determine overall status from files
    const determineOverallStatus = useCallback((files: { status: string }[]) => {
        // If there are no files, the workflow is just starting
        if (files.length === 0) {
            return 'in_progress';
        }

        if (files.some((file) => file.status.toLowerCase() === 'failed')) {
            return 'failed';
        } else if (
            files.some(
                (file) =>
                    file.status.toLowerCase() === 'in_progress' ||
                    file.status.toLowerCase() === 'processing' ||
                    !file.status ||
                    file.status.toLowerCase() === 'unspecified',
            )
        ) {
            return 'in_progress';
        } else if (files.every((file) => file.status.toLowerCase() === 'succeeded')) {
            return 'succeeded';
        }
        return 'unspecified';
    }, []);

    // Load workflows
    const refreshExistingWorkflows = useCallback(async () => {
        if (!experimentId) return;

        try {
            setIsLoadingWorkflows(true);

            // Get all workflows
            const workflows = await getWorkflows(experimentId);

            if (workflows && workflows.length > 0) {
                // Get all files from all workflows
                const workflowFilesMap = await getAllWorkflowFiles(experimentId);

                // Convert to our WorkflowStatus format
                const workflowStatuses: WorkflowStatus[] = [];

                for (const workflow of workflows) {
                    const files = workflowFilesMap.get(workflow.session_id) || [];
                    const overallStatus = determineOverallStatus(files);

                    workflowStatuses.push({
                        sessionId: workflow.session_id,
                        createdAt: workflow.created_at,
                        files: files.map((file) => ({
                            fileName: file.file_name,
                            status: file.status,
                        })),
                        overallStatus,
                    });
                }

                // Sort workflows by creation date (newest first)
                workflowStatuses.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

                // Set all workflows
                setExistingWorkflows(workflowStatuses);
            } else {
                setExistingWorkflows([]);
            }
        } catch (error) {
            console.error('Error refreshing existing workflows:', error);
        } finally {
            setIsLoadingWorkflows(false);
        }
    }, [experimentId, getWorkflows, getAllWorkflowFiles, determineOverallStatus]);

    // Check status of a specific workflow
    const checkWorkflowStatus = useCallback(
        async (expId: string, sessionId: string) => {
            try {
                // Get the current status
                const status = await getWorkflowStatus(expId, sessionId);

                // Update the workflow in the existing workflows list
                setExistingWorkflows((prevWorkflows) => {
                    return prevWorkflows.map((workflow) => {
                        if (workflow.sessionId === sessionId) {
                            return {
                                ...workflow,
                                files: status.status.map((file) => ({
                                    fileName: file.file_name,
                                    status: file.status,
                                })),
                                overallStatus: status.overall_status,
                            };
                        }
                        return workflow;
                    });
                });

                return status.overall_status;
            } catch (error) {
                console.error('Error checking workflow status:', error);
                return 'error';
            }
        },
        [getWorkflowStatus],
    );

    return {
        existingWorkflows,
        isLoadingWorkflows,
        refreshExistingWorkflows,
        checkWorkflowStatus,
    };
};

export default useSynapseWorkflows;
